import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FilterService, FilterState, FilterStateImpl } from '@app/core/services/container/filter.service';
import { StateFactoryService } from '@app/core/services/state/state-factory.service';
import { TableDataSource } from '@app/shared/components/data/data-table/data-table.datasource';
import { Constants, Icons } from '@app/shared/constants';
import { FieldOption } from '@app/shared/models/field.interface';
import { FilterItem, FiltersOutput } from '@app/shared/models/filter.interface';
import { Entity } from '@app/shared/models/models.interface';
import { SortParam } from '@app/shared/models/page.interface';
import { StateService } from '@app/shared/services/state-service.interface';
import { ButtonToggleOptionIcon } from '@components/buttons/button-toggle-group/button-toggle-group.component';
import { InputSearchComponent } from '@components/form-controls/inputs/input-search/input-search.component';
import { Subject } from 'rxjs';
import { EntitySelectionModel } from '../data-table-new/entitySelectionModel';
import { BaseViewComponent } from './base-view/base-view.component';
//import { GridViewComponent } from './grid-view/grid-view.component';

/** Column alignment */
export enum COLUMN_ALIGN {
  LEFT = 0,
  CENTER = 1,
  RIGHT = 2
}

/** Column definition */
export interface Column {
  name: string; // internal column id
  label: string; // description
  sortable?: boolean; // column is sortable
  sticky?: boolean; // fixed column
  hidden?: boolean; // hidden column
  action?: boolean; // column triggers an action
  actionIcon?: string; // action icon
  extraClass?: string; // extra class to add
  searchable?: boolean; // the column is searchable in filters
  isCheckbox?: boolean; // the column is a checkbox
  isToggle?: boolean; // the column is a toggle
  isStatus?: boolean; // the column is a status active/inactive
  isImage?: boolean; // the columns contains an image
  isTemplate?: boolean; // the column must load a template
  align?: COLUMN_ALIGN; // alignment (see COLUMN_ALIGN)
  isUTC?: boolean; // the columns contains an UTC value
  isDefault?: boolean; // the column is a default
  templateRef?: TemplateRef<any>;
  fieldType?: string; // optional field type
}

/** Interface used when the column click event is emitted */
export interface ColumnClick {
  column: Column; // the column that has been clicked
  data: any; // the row (with all the data)
}

export interface SelectRowClick {
  row: any; // the selected row (as represented by the Entity)
}

/** Data table state */
export interface DataTableState {
  viewMode: string;
  initialPage: string;
  currentIndex: number;
  currentSize: number;
  sort: SortParam;
}

export class DataTableStateImpl implements DataTableState {
  viewMode = DataTableComponent.VIEW_TYPE.GRID;
  initialPage = '0';
  currentIndex = 0;
  currentSize = 10;
  sort = { field: '', direction: Constants.ASC_DIR };
}

/**
 * A template card footer item
 */
export interface TemplateInfoFooterItem {
  label?: string;
  value: string;
  className?: string; //Optional class name
}

/**
 * An item to show in the card
 */
export interface TemplateInfoValue {
  label?: string; //optional because some designs lack of this...
  value: string;
  icon?: string; //icon name
  className?: string; //optional class name
}

/** A group of items */
export interface TemplateInfoGroupValue {
  className?: string; //optional class name
  columns: Array<TemplateInfoValue>;
}

/**
 * The template card definition
 */
export interface TemplateInfo {
  title: string;
  subtitle?: string | {};
  className?: string;
  active?: boolean;
  picture?: any; //optional picture
  entity?: Entity | any; //the entity that triggers click events
  multiSelectActive?: boolean;
  footer?: Array<TemplateInfoFooterItem>; //optional footer items
  menuOptions?: []; //optional menu options
  content: Array<TemplateInfoGroupValue>; //the card contents
}

export interface ContextMenuOptionSelected {
  option: ContextMenuOption;
  entity: Entity;
}

export interface ContextMenuOption {
  id: string;
  title: string;
  icon: string;
  permissions?: Array<string> | string;
}

@Component({
  selector: 'vfs-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss']
})
export class DataTableComponent<T extends Entity, S = any> implements OnInit, OnDestroy {
  /** View mode types */
  static VIEW_TYPE = {
    GRID: 'grid',
    CARD: 'card',
    LIST: 'list'
  };

  viewOptions: ButtonToggleOptionIcon[] = [];
  @Input() id: string;
  @Input() dataSource: TableDataSource<T, S>;
  @Input() columns: Column[] = [];
  @Input() sortColumn = '';
  @Input() sortDirection = '';
  @Input() allowMultiSelect = false;
  @Output() columnClicked = new EventEmitter<ColumnClick>();
  @Output() selectRowClicked = new EventEmitter<SelectRowClick>();
  @Input() cardItemTemplate: TemplateRef<any>; // TODO: Elimitate this and use a generic dataItemTemplate
  @Input() dataItemTemplate: TemplateRef<any>;
  @Input() multiSelectTemplate: TemplateRef<any>;
  @Input() defaultView: string;
  @Input() multiSelectActive = false;
  @Input() hideMultiSelectCheckbox = false;
  @Input() initialSelection: EntitySelectionModel<T>;
  @Input() selection: EntitySelectionModel<T> = null;
  @Output() selectionChange = new EventEmitter(true);
  @ViewChild('searchBox') searchBox: InputSearchComponent;
  @Output() searchEmitter = new EventEmitter<string>();
  @Output() mySearchValue: string = '';
  @Input() hasHorizontalScroll = false;
  @Input() canSearch = true;
  @Input() canFilter = true;
  @Input() canChangeView = true;
  @Input() hasActions = true;
  @Input() hasPagination = true;
  @Output() multiSelectChanged = new EventEmitter<boolean>();
  @Input() filters: FilterItem[] = [];
  @Output() applyFilters = new EventEmitter<FiltersOutput>();
  @Input() service: StateService<T, S> = null;
  @Input() sortIcon: string = Icons.SORT_ASC;
  @Input() sortColumsForCardView: FieldOption[] = [];
  @Input() headerButtonRoute: Router;
  @Input() headerButtonRouteParams: any;
  @Input() routeText: string;
  @ViewChild(BaseViewComponent) public baseView: BaseViewComponent<T, S>;
  //@ViewChild('gridView', { static: false }) public gridView: GridViewComponent<T, S>;

  // context menu
  @Input() contextMenuOptions: Array<ContextMenuOption> = [];
  @Output() contextMenuOptionSelected = new EventEmitter<ContextMenuOptionSelected>();

  // input-search #searchBox
  showSearch: boolean = true;
  isSearching: boolean = false;
  protected destroyed = new Subject();

  // empty-state message
  @Input() emptyText: string;
  @Input() emptyHint: string;

  /**
   * Constructor
   */
  constructor(private filterService: FilterService, protected stateFactory: StateFactoryService) {}

  /**
   * Initialize the component
   */
  ngOnInit() {
    // Create the selection model
    this.selection = new EntitySelectionModel<T>(
      this.allowMultiSelect,
      this.initialSelection ? this.initialSelection.selected : []
    );
    this.selectionChange.emit(this.selection); // emit the change...

    /** Set the sort icon (only card view mode) */
    if (this.getDataTableState().sort) {
      if (this.getDataTableState().sort.direction === Constants.ASC_DIR) {
        this.sortIcon = Icons.SORT_ASC;
      } else {
        this.sortIcon = Icons.SORT_DESC;
      }
    } else this.sortIcon = Icons.SORT_ASC;
    this.sortColumsForCardView = this.getSortColumns();

    // Set the current view mode
    if (!this.defaultView && this.getDataTableState()) {
      this.defaultView = this.getDataTableState().viewMode;
    }
    this.changeView(this.defaultView);

    this.viewOptions = [
      {
        icon: Icons.VIEW_LIST,
        value: DataTableComponent.VIEW_TYPE.GRID,
        checked: this.defaultView === DataTableComponent.VIEW_TYPE.GRID
      },
      {
        icon: Icons.VIEW_MODULE,
        value: DataTableComponent.VIEW_TYPE.CARD,
        checked: this.defaultView === DataTableComponent.VIEW_TYPE.CARD
      }
    ];
  }

  /**
   * Click on a cell. Emit a value to the suscriber
   *
   * @param value the value to emit
   */
  clickOnAction(value: ColumnClick) {
    this.columnClicked.emit(value);
  }

  /**
   * Select a row emitter "callback"
   */
  selectRow(value: SelectRowClick) {
    this.selectRowClicked.emit(value);
  }

  /**
   * Change the view mode
   *
   * @param type the view mode
   */
  changeView(type: any) {
    if (this.selection) {
      this.selection.clear();
    }
    this.defaultView = type;
    if (this.getDataTableState()) {
      this.getDataTableState().viewMode = type;
      this.service.saveState();
    }
  }

  /**
   * Get the datatable state
   */
  private getDataTableState(): DataTableState {
    return this.service.getDataTableState(this.id);
  }

  /**
   * Toggle multiselect actions
   */
  toggleMultiselect() {
    if (this.selection && this.multiSelectActive) {
      this.selection.clear();
    }
    this.multiSelectActive = !this.multiSelectActive;
    this.multiSelectChanged.emit(this.multiSelectActive);
  }

  /**
   * Destroy the component
   */
  ngOnDestroy(): void {
    if (this.dataSource) {
      this.dataSource.close();
    }

    this.destroyed.next();
  }

  /**
   * Show the filters and emit an event ONLY if the user apply them
   *
   * IMPORTANT: the service must define a FilterState
   */
  showFilters() {
    let fstate: FilterState = this.service.getFilterState(this.id);
    if (!fstate) {
      fstate = new FilterStateImpl();
    }
    this.filters.forEach((item: FilterItem) => {
      if (fstate.value[item.field.formControlName]) {
        item.value = fstate.value[item.field.formControlName].value;
      }
    });

    this.filterService.openWithData(this.filters).subscribe((filtersOut: FiltersOutput) => {
      if (filtersOut.accept) {
        filtersOut.filters.forEach(f => {
          fstate.value[f.key] = f;
        });
        // save the filters
        this.service.setFilterState(fstate);

        // propagate the filters...
        this.applyFilters.emit(filtersOut);
      }
    });
  }

  /**
   * Get the sortable columns
   *
   * @return an array of FieldOptions
   */
  getSortColumns(): FieldOption[] {
    let cols: FieldOption[] = [];
    this.columns.forEach(col => {
      if (col.sortable) {
        let f: FieldOption = {
          label: col.label,
          value: col.name
        };
        cols.push(f);
      }
    });
    return cols;
  }

  /**
   * Card is changing sort direction
   */
  cardViewChangeSortDirection() {
    if (this.getDataTableState().sort) {
      if (this.getDataTableState().sort.direction === Constants.ASC_DIR) {
        this.getDataTableState().sort.direction = Constants.DESC_DIR;
        this.sortIcon = Icons.SORT_DESC;
      } else {
        this.getDataTableState().sort.direction = Constants.ASC_DIR;
        this.sortIcon = Icons.SORT_ASC;
      }
      this.service.saveState();
      this.reload();
    }
  }

  /**
   * Change sort column
   *
   * @param columnt the column to sort by
   */
  changeSortColumn(column: string) {
    if (column !== '') {
      if (this.getDataTableState().sort) {
        this.getDataTableState().sort.field = column;
        this.service.saveState();
        this.reload();
      }
    }
  }

  /**
   * Reload the view
   */
  reload() {
    this.dataSource.getAll(
      this.stateFactory.getInitialPage(this.service),
      this.stateFactory.getInitialPageSize(this.service),
      [this.getDataTableState().sort],
      this.stateFactory.getAppliedFilters(this.service, this.filterService, this.id)
    );
  }

  /**
   * Emit the option
   *
   * @param option the option
   */
  onContextMenuAction(value: ContextMenuOptionSelected) {
    this.contextMenuOptionSelected.emit(value);
  }

  onInputSearchChange() {
    if (this.canSearch) {
      this.mySearchValue = this.searchBox.inputRef.nativeElement.value;
    }
  }
}
